import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Repeat } from "lucide-react";
import { toast } from "sonner";
import IvaFields from "@/components/IvaFields";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import api, { formatApiError } from "@/lib/api";
import Field from "./Field";
import MultiIvaSection from "./MultiIvaSection";

const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS", "COP", "CLP", "BRL", "CHF", "JPY"];

/**
 * Rejilla compartida de campos del formulario de subida de factura/ingreso/salario.
 * Todo el estado (`form`) y el setter viven en el padre; este componente sólo
 * pinta los campos y adapta las etiquetas al contexto (ingreso vs gasto vs salario).
 *
 * `onCategoryCreated(name)` (opcional): callback invocado con el nombre de la
 * nueva categoría cuando el usuario la crea desde el dropdown. El padre debe
 * recargar la lista de categorías y ajustar `form.category` al nuevo nombre.
 * `allowRecurring` (opcional, default false): muestra el toggle "Repetir cada
 * mes" + input "Hasta". El padre lee `form.repeat_until` al guardar.
 */
export default function InvoiceFormFields({
  form,
  setForm,
  categories,
  isIncome,
  isSalary,
  vendorLabel,
  onCategoryCreated,
  allowRecurring = false,
}) {
  const { t, lang } = useI18n();
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);

  const createCategory = async () => {
    const name = (newCatName || "").trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      const kind = isSalary ? "salary" : isIncome ? "income" : "other";
      await api.post("/categories", {
        name,
        label_es: name,
        label_en: name,
        kind,
      });
      toast.success(lang === "es" ? "Categoría creada" : "Category created");
      setForm({ ...form, category: name.toLowerCase() });
      setNewCatName("");
      setNewCatOpen(false);
      if (onCategoryCreated) onCategoryCreated(name.toLowerCase());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingCat(false);
    }
  };

  const handleMultiIvaChange = (bd) => {
    if (Array.isArray(bd) && bd.length >= 2) {
      const sumBase = bd.reduce((acc, b) => acc + (parseFloat(b.base) || 0), 0);
      const sumTax = bd.reduce((acc, b) => acc + (parseFloat(b.tax) || 0), 0);
      const total = sumBase + sumTax;
      setForm((prev) => ({
        ...prev,
        iva_breakdown: bd,
        subtotal: sumBase.toFixed(2),
        tax: sumTax.toFixed(2),
        amount: total.toFixed(2),
        tax_rate: "", // mixto
      }));
    } else {
      setForm((prev) => ({ ...prev, iva_breakdown: bd }));
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4 mt-4">
      <Field label={vendorLabel} testid="form-vendor">
        <Input
          value={form.vendor}
          onChange={(e) => setForm({ ...form, vendor: e.target.value })}
          data-testid="input-vendor"
        />
      </Field>
      <Field label={isSalary ? t("salaryDate") : t("date")} testid="form-date">
        <Input
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          data-testid="input-date"
        />
      </Field>

      {!(isIncome || isSalary) && (
        <div className="col-span-2">
          <Field label={t("vendorTaxId")} testid="form-vendor-tax-id">
            <Input
              value={form.vendor_tax_id}
              onChange={(e) => setForm({ ...form, vendor_tax_id: e.target.value.toUpperCase().replace(/\s+/g, "") })}
              data-testid="input-vendor-tax-id"
              placeholder="B12345678"
              className="font-mono"
            />
          </Field>
        </div>
      )}

      <div className="col-span-2 flex items-center gap-2 -mb-2">
        <input
          id="upload-is-credit-note"
          type="checkbox"
          checked={!!form.is_credit_note}
          onChange={(e) => setForm({ ...form, is_credit_note: e.target.checked })}
          data-testid="input-is-credit-note"
          className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
        />
        <Label htmlFor="upload-is-credit-note" className="text-sm font-normal cursor-pointer" title={t("creditNoteHelp")}>
          {t("creditNote")}
        </Label>
      </div>

      {!isSalary && (
        <IvaFields
          values={{ subtotal: form.subtotal, tax: form.tax, amount: form.amount, tax_rate: form.tax_rate }}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          testidPrefix="iva"
        />
      )}

      {!isSalary && (
        <div className="col-span-2">
          <MultiIvaSection
            breakdown={form.iva_breakdown}
            lang={lang}
            onChange={handleMultiIvaChange}
          />
        </div>
      )}

      {isSalary && (
        <div className="col-span-2">
          <Field label={lang === "es" ? "Importe neto" : "Net amount"} testid="form-salary-amount">
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  amount: v,
                  subtotal: v,
                  tax: "0",
                  tax_rate: "0",
                }));
              }}
              placeholder="0.00"
              className="font-mono-num"
              data-testid="input-salary-amount"
            />
          </Field>
        </div>
      )}

      <Field label={t("currency")} testid="form-currency">
        <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
          <SelectTrigger data-testid="select-currency"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CURRENCIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {!isIncome && (
        <Field label={t("category")} testid="form-category">
          {newCatOpen ? (
            <div className="flex gap-2">
              <Input
                autoFocus
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    createCategory();
                  } else if (e.key === "Escape") {
                    setNewCatOpen(false);
                    setNewCatName("");
                  }
                }}
                placeholder={lang === "es" ? "Nombre de la categoría" : "Category name"}
                data-testid="input-new-category-name"
                disabled={creatingCat}
              />
              <Button
                type="button"
                size="sm"
                onClick={createCategory}
                disabled={creatingCat || !newCatName.trim()}
                data-testid="save-new-category-btn"
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0"
              >
                {creatingCat ? "..." : (lang === "es" ? "Crear" : "Create")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setNewCatOpen(false); setNewCatName(""); }}
                disabled={creatingCat}
                data-testid="cancel-new-category-btn"
                className="shrink-0"
              >
                {t("cancel")}
              </Button>
            </div>
          ) : (
            <Select
              value={form.category}
              onValueChange={(v) => {
                if (v === "__new__") {
                  setNewCatOpen(true);
                } else {
                  setForm({ ...form, category: v });
                }
              }}
            >
              <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    {categoryLabel(c, lang)}
                  </SelectItem>
                ))}
                <SelectItem value="__new__" data-testid="new-category-option">
                  <div className="flex items-center gap-2 text-[var(--primary)]">
                    <Plus size={14} />
                    <span>{lang === "es" ? "Nueva categoría…" : "New category…"}</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      )}

      <Field label={t("type")} testid="form-type">
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
          <SelectTrigger data-testid="select-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="expense">{t("expense")}</SelectItem>
            <SelectItem value="income">{t("income")}</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {!isSalary && (
        <Field label={t("invoiceNumber")}>
          <Input
            value={form.invoice_number}
            onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
            data-testid="input-invoice-number"
          />
        </Field>
      )}

      <div className="col-span-2">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("notes")}</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="input-notes"
          rows={2}
          className="mt-1.5"
        />
      </div>

      {allowRecurring && !isSalary && (
        <div className="col-span-2 mt-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              id="upload-recurring"
              type="checkbox"
              checked={!!form.recurring}
              onChange={(e) =>
                setForm({
                  ...form,
                  recurring: e.target.checked,
                  ...(e.target.checked ? {} : { repeat_until: "" }),
                })
              }
              data-testid="input-recurring"
              className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <Label htmlFor="upload-recurring" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
              <Repeat size={14} className="text-[var(--primary)]" />
              {lang === "es" ? "Repetir este gasto cada mes" : "Repeat this expense every month"}
            </Label>
          </div>
          {form.recurring && (
            <div className="pl-6 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "Hasta el (inclusive)" : "Until (inclusive)"}
              </Label>
              <Input
                type="date"
                value={form.repeat_until || ""}
                min={form.date}
                onChange={(e) => setForm({ ...form, repeat_until: e.target.value })}
                data-testid="input-repeat-until"
                className="max-w-xs"
              />
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es"
                  ? "Se generará una copia el mismo día de cada mes hasta la fecha indicada (máx. 24 copias)."
                  : "One copy on the same day of each month until that date (max 24 copies)."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
